import React, { useEffect, useMemo, useRef } from "react";
import HClusteredBarChart from "./BarCharts/HBarCharts/HClusteredBarChart";
import VerticalNestedLineChart from "./LineCharts/HLineChart/HClusteredLineChart";
import HStackedBarChart from "./BarCharts/HBarCharts/HStackedBarChart";
import VClusteredBarChart from "./BarCharts/VBarCharts/VClusteredBarChart";
import VStackedBarChart from "./BarCharts/VBarCharts/VStackedBarChart";
import HorizontalBarChartSkeleton from "../components/SkeletonLoader/HorizontalBarChartSkeleton";
import HLineChart from "./LineCharts/HLineChart/HLineChart";
import { chartConfig } from "./chartConfig";
import VLineChart from "./LineCharts/VLineChart/VLineChart";
import TableAsChart from "./TableAsChart";
import ChartError from "./ChartError";
import { ChartRenderedFromType, IChartResponse } from "src/pages/Insights/models/interfaces";
import { useDispatch } from "react-redux";
import { setIsMultiTileSlide } from "src/pages/Insights/states/quantDashboardSlice";
import { ChartScaleProvider, TooSmallFallback, useScaledConfig } from "./ChartScaleContext";
import { useContainerWidth } from "src/components/Hooks/useContainerWidth";
import { useContainerHeight } from "src/components/Hooks/useContainerHeight";
import { computeLayoutFromCase, normalizeViewLayout, ResolvedTile } from "../chartHelper";

import { TileInfoWrapper, useTileInfoHover } from "./TileInfoToggle";
// import "./TileInfoToggle/tile-info-toggle.css";

// ─────────────────────────────────────────────
// Interfaces (unchanged)
// ─────────────────────────────────────────────

export interface AxisIdentifier {
	x: string;
	y: string;
	z1: string | null;
	z2: string | null;
}

export interface ChartPosition {
	transpose: boolean;
	row?: number;
	col?: number;
}

export interface Footnote {
	q_txt: string;
	src: string;
	other: string;
	stat_message?: string;
	stat_significance_message?: string;
	stat_test_performed?: boolean;
}

export interface SeriesPoint {
	name: string;
	originalName: string;
	value: number | string;
	n_size: number | string;
}

export interface ChartRow {
	xAxis: string;
	xAxisOriginal: string;
	series: SeriesPoint[];
	n_size: {
		min: number;
		max: number | null;
	};
}

export type ChartDataEntry = {
	chart_data: ChartRow[] | Record<string, ChartRow[]>;
};

export type ChartData = ChartDataEntry;

export interface ChartConfig {
	q_num: string;
	sect_name: string;
	x_vis: "Yes" | "No";
	y_vis: "Yes" | "No";
	h_grid: "Yes" | "No";
	v_grid: "Yes" | "No";
	x_lbl: string;
	y_lbl: string;
	sort_chart_data: string;
	chart_title_font_size: number;
	legend_name: Record<string, string>;
	show_legend: "Yes" | "No";
	legend_pos: "Top" | "Bottom" | "Left" | "Right";
	show_dlabels: "Yes" | "No";
	dlabel_size: number;
	dlabel_color: string;
	dlabel_fmt: string;
	dlabel_pos: "Inside" | "Outside" | "Right" | "Left";
	ser_fill: Record<string, unknown>;
	ser_color: Record<string, string>;
	lc_line: Record<string, unknown>;
	lc_marker_sp: Record<string, string>;
	lc_color: Record<string, unknown>;
	lc_marker_size: Record<string, unknown>;
	line_chart_line_weight: number;
	line_smoothening: "Yes" | "No";
	confidence?: Record<string, unknown>;
	stat_test_config?: unknown;
	auto_populate_ser_color?: boolean;
}

export interface ChartModel {
	chart_title: string;
	chart_type: string;
	chart_subtitle?: string;
	analysis_type: string;
	analysis_id: string;
	slide_name?: string | null;
	normalized_slide_key?: string | null;
	position_cells?: string | null;
	layout_case?: string | null;
	view_layout?: string | null;
	view_position?: number | null;
	view_chart_count?: number | null;
	value_params: unknown[];
	is_weight: boolean;
	is_splitting?: boolean;
	is_grid: boolean;
	axis_idf: AxisIdentifier;
	include_blanks: boolean;
	question_number: string;
	x_axis_values: string[];
	y_axis_values: string[];
	x_axis_label: string;
	y_axis_label: string;
	position: ChartPosition;
	footnote: Footnote;
	filter_hierarchy: {
		dimension: string;
		order: number;
		orientation: "row" | "column";
		values: string[];
		field?: string;
	}[];
	data: ChartData[];
	chart_config: ChartConfig;
	insights_id: string;
	stat_df_grouping_cols?: string[];
}

export interface ApiError {
	is_error: boolean;
	is_retry: boolean;
	message?: string;
	error_message?: string;
	error_type?: string;
}

export interface BarMockResponse {
	_id?: string;
	message: string;
	data: DashboardData;
	error: null | ApiError;
}

export interface DashboardData {
	sections: Section[];
	from_cache: boolean;
	error?: ApiError;
}

export interface Section {
	name: string;
	slide_name?: string;
	view_layout?: string;
	charts: ChartModel[] | ChartModel[][];
	layout_case?: string | null;
}

export interface DynamicChartRenderProps {
	chartsResponse: any;
	isChartView: boolean;
	setIsChartView: (value: boolean) => void;
	setChartData: (value: IChartResponse | null) => void;
	setIsChartLoading: (value: boolean) => void;
	renderedFrom?: ChartRenderedFromType;
	isAISummaryAvailable?: boolean;
	skipMultiTileDispatch?: boolean;
}

// ─────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────

/**
 * Check if a ChartModel has renderable chart_data.
 * Returns false when data is missing, empty, or chart_data is absent/empty.
 */
export function hasRenderableChartData(chart: ChartModel): boolean {
	if (!chart.data || !Array.isArray(chart.data) || chart.data.length === 0) return false;

	const firstEntry = chart.data[0];
	if (!firstEntry?.chart_data) return false;

	// chart_data can be an array or a Record<string, ChartRow[]>
	if (Array.isArray(firstEntry.chart_data)) {
		return firstEntry.chart_data.length > 0;
	}

	// Record — check if at least one key has data
	return Object.keys(firstEntry.chart_data).length > 0;
}

export const getChartRulesFromConfig = () => {
	const hClusteredConfig = chartConfig.getHorizontalClusteredConfig();
	const vClusteredConfig = chartConfig.getVerticalClusteredConfig();
	const vStackedConfig = chartConfig.getVerticalStackedConfig();
	const hLineConfig = chartConfig.getHorizontalLineConfig();
	const vLineConfig = chartConfig.getVerticalLineConfig();

	return {
		cluster_bar: {
			yAxisLabelWidth: {
				min: hClusteredConfig.yAxisLabel.minWidth,
				max: hClusteredConfig.yAxisLabel.maxWidth,
			},
		},
		stacked_column: { chartColumnWidth: { min: vStackedConfig.minColumnWidth } },
		cluster_column: { chartColumnWidth: { min: vClusteredConfig.clusterBarWidth } },
		line_chart: {
			chartColumnWidth: { min: hLineConfig.minColumnWidth },
			chartHeight: hLineConfig.chartHeight,
			attributesRowHeight: hLineConfig.attributesRowHeight,
		},
		vertical_line_chart: {
			chartRowHeight: { min: vLineConfig.minRowHeight },
			chartWidth: vLineConfig.chartWidth,
			attributesColWidth: vLineConfig.attributesColWidth,
		},
	};
};

function flattenCharts(rawCharts: ChartModel[] | ChartModel[][]): ChartModel[] {
	if (!rawCharts || rawCharts.length === 0) return [];
	if (Array.isArray(rawCharts[0])) {
		return (rawCharts as ChartModel[][])[0] ?? [];
	}
	return rawCharts as ChartModel[];
}

function checkIsMultiTileSlide(charts: ChartModel[], sectionLayoutCase?: string | null): boolean {
	if (charts.length <= 1) return false;
	const first = charts[0];
	if (!first) return false;
	if (first.view_chart_count && first.view_chart_count > 1) return true;
	if (sectionLayoutCase) {
		const firstName = first.slide_name;
		return !!firstName && charts.every((c) => c.slide_name === firstName);
	}
	const firstName = first.slide_name;
	if (!firstName) return false;
	return charts.every((c) => c.slide_name === firstName && typeof c.position_cells === "string" && c.position_cells.trim().length > 0);
}

function resolveLayoutCase(section: Section | null, charts: ChartModel[], topLevelViewLayout?: string | null): string {
	const first = charts[0];
	if (topLevelViewLayout) {
		const chartCount = first?.view_chart_count ?? charts.length;
		return normalizeViewLayout(topLevelViewLayout, chartCount);
	}
	if (first?.view_layout) {
		const chartCount = first.view_chart_count ?? charts.length;
		return normalizeViewLayout(first.view_layout, chartCount);
	}
	if (section?.layout_case) return section.layout_case;
	if (first?.layout_case) return first.layout_case;
	return "case1";
}

// ─────────────────────────────────────────────
// SlideHeader
// ─────────────────────────────────────────────

interface SlideHeaderProps {
	title: string;
}

const SlideHeader: React.FC<SlideHeaderProps> = ({ title }) => (
	<div className="slide-header">
		<h2 className="slide-header__title">{title}</h2>
	</div>
);

// ─────────────────────────────────────────────
// ChartTile — renders a single chart by type
// ─────────────────────────────────────────────

export interface ChartTileProps {
	chartData: ChartModel;
	commonProps: {
		chartRules: ReturnType<typeof getChartRulesFromConfig>;
		isAISummaryAvailable: boolean;
		isChartView: boolean;
		setIsChartView: (v: boolean) => void;
	};
	isCompact?: boolean;
	renderedFrom?: ChartRenderedFromType;
}

const ChartTile: React.FC<ChartTileProps> = ({ chartData, commonProps, isCompact = false, renderedFrom = ChartRenderedFromType.Dashboard }) => {
	const props = { chartResponse: chartData, ...commonProps };

	const chart = (() => {
		const verticalLineRules = commonProps.chartRules.vertical_line_chart;
		switch (chartData.chart_type) {
			case "Cluster_Column":
				return <VClusteredBarChart {...props} renderedFrom={renderedFrom} />;
			case "Cluster_Bar":
				return <HClusteredBarChart {...props} renderedFrom={renderedFrom} />;
			case "Stacked_Column":
				return <VStackedBarChart {...props} renderedFrom={renderedFrom} />;
			case "Stacked_Bar":
				return <HStackedBarChart {...props} renderedFrom={renderedFrom} />;
			case "Line_Horizontal":
				return <HLineChart {...props} renderedFrom={renderedFrom} />;
			case "Line_Vertical":
				return <VLineChart {...props} renderedFrom={renderedFrom} />;
			case "Table":
				return <TableAsChart {...props} renderedFrom={renderedFrom} />;
			default:
				return <ChartError errorMessage={`${chartData.chart_type} chart type is not supported yet. Coming soon.`} is_retry={false} />;
		}
	})();

	if (!isCompact) return <>{chart}</>;

	return (
		<div className="multi-tile__tile">
			<div className="multi-tile__tile-canvas">{chart}</div>
		</div>
	);
};

// ─────────────────────────────────────────────
// ScaledChartTile
// ─────────────────────────────────────────────

const ScaledChartTile: React.FC<{
	chartData: ChartModel;
	commonProps: ChartTileProps["commonProps"];
	renderedFrom: ChartRenderedFromType;
}> = ({ chartData, commonProps, renderedFrom = ChartRenderedFromType.Dashboard }) => {
	const { isTooSmall } = useScaledConfig();
	const { isInfoVisible, show, hide } = useTileInfoHover();

	if (isTooSmall) {
		return <TooSmallFallback title={chartData.chart_title} />;
	}

	// Hide the info icon when chart has no renderable data
	const showInfoIcon = hasRenderableChartData(chartData);

	return (
		<TileInfoWrapper isInfoVisible={isInfoVisible} onShow={show} onHide={hide} chartData={chartData} showInfoIcon={showInfoIcon}>
			<ChartTile chartData={chartData} commonProps={commonProps} isCompact renderedFrom={renderedFrom} />
		</TileInfoWrapper>
	);
};

// ─────────────────────────────────────────────
// ScaledTile
// ─────────────────────────────────────────────

export interface ScaledTileProps {
	tile: ResolvedTile;
	commonProps: ChartTileProps["commonProps"];
	wrapperWidth: number;
	wrapperHeight: number;
	renderedFrom?: ChartRenderedFromType;
}

export const ScaledTile: React.FC<ScaledTileProps> = ({ tile, commonProps, wrapperWidth, wrapperHeight, renderedFrom }) => {
	return (
		<ChartScaleProvider wrapperWidth={wrapperWidth} wrapperHeight={wrapperHeight} tileWidth={tile.tileWidthPx} tileHeight={tile.tileHeightPx}>
			<ScaledChartTile chartData={tile.chart} commonProps={commonProps} renderedFrom={renderedFrom} />
		</ChartScaleProvider>
	);
};

// ─────────────────────────────────────────────
// AutoLayoutBanner
// ─────────────────────────────────────────────

const AutoLayoutBanner: React.FC = () => (
	<div className="multi-tile__auto-layout-banner" role="alert">
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
			<path
				fillRule="evenodd"
				d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.5 1.5 0 0 1-1.299 2.25H2.804a1.5 1.5 0 0 1-1.3-2.25l5.197-9ZM8 4a.75.75 0 0 1 .75.75v3a.75.75 0 1 1-1.5 0v-3A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
				clipRule="evenodd"
			/>
		</svg>
		Layout case not recognized — using default layout.
	</div>
);

// ─────────────────────────────────────────────
// MultiTileGrid — case-based layout
// ─────────────────────────────────────────────

const COLUMN_GAP = 8;
const ROW_GAP = 18;
const GAP = 6;
interface MultiTileGridProps {
	charts: ChartModel[];
	layoutCase: string;
	commonProps: ChartTileProps["commonProps"];
	renderedFrom?: ChartRenderedFromType;
}

export const MultiTileGrid: React.FC<MultiTileGridProps> = ({ charts, layoutCase, commonProps, renderedFrom }) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const wrapperWidth = useContainerWidth(wrapperRef);
	const wrapperHeight = useContainerHeight(wrapperRef);

	const resolved = useMemo(() => computeLayoutFromCase(charts, layoutCase, wrapperWidth, wrapperHeight, ROW_GAP, COLUMN_GAP), [charts, layoutCase, wrapperWidth, wrapperHeight]);

	const gridStyle: React.CSSProperties = useMemo(
		() => ({
			display: "grid",
			gridTemplateColumns: resolved.layoutCase.gridTemplateColumns,
			gridTemplateRows: resolved.layoutCase.gridTemplateRows,
			rowGap: `${ROW_GAP}px`,
			columnGap: `${COLUMN_GAP}px`,
			width: "100%",
			height: "100%",
			minHeight: 0,
		}),
		[resolved.layoutCase],
	);

	return (
		<div ref={wrapperRef} className="multi-tile__wrapper" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
			{resolved.fallbackApplied && <AutoLayoutBanner />}

			<div style={gridStyle}>
				{resolved.tiles.map((tile) => (
					<div
						key={tile.chart.analysis_id}
						style={{
							gridRow: tile.placement.gridRow,
							gridColumn: tile.placement.gridColumn,
							minWidth: 0,
							minHeight: 0,
							overflow: "visible",
						}}>
						<ScaledTile tile={tile} commonProps={commonProps} wrapperWidth={wrapperWidth} wrapperHeight={wrapperHeight} renderedFrom={renderedFrom} />
					</div>
				))}
			</div>
		</div>
	);
};

// ─────────────────────────────────────────────
// InlineScaleWrapper — provides ChartScaleProvider for
// AskKuralInline single-chart renders so useScaledConfig()
// returns a scaled-down config instead of IDENTITY_SCALE.
// ─────────────────────────────────────────────

const InlineScaleWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const wrapperWidth = useContainerWidth(wrapperRef);
	const wrapperHeight = useContainerHeight(wrapperRef);

	// Use the chartConfig reference dimensions as the "full-size" baseline.
	// The actual container will be smaller, producing scaleFactor < 1.
	const cfg = chartConfig.getMultiTileScalingConfig();
	const refWidth = cfg.reference.width;
	const refHeight = cfg.reference.height;

	return (
		<div ref={wrapperRef} className="multi-tile__wrapper" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
			<ChartScaleProvider
				wrapperWidth={refWidth}
				wrapperHeight={refHeight}
				tileWidth={wrapperWidth}
				tileHeight={wrapperHeight}
			>
				{children}
			</ChartScaleProvider>
		</div>
	);
};

// ─────────────────────────────────────────────
// DynamicChartRender — main export
// ─────────────────────────────────────────────

const DynamicChartRender: React.FC<DynamicChartRenderProps> = ({
	chartsResponse,
	isChartView,
	setIsChartView,
	setChartData,
	setIsChartLoading,
	isAISummaryAvailable = false,
	skipMultiTileDispatch = false,
	renderedFrom = ChartRenderedFromType.Dashboard,
}) => {
	const dispatch = useDispatch();

	const section: Section | null = useMemo(() => {
		if (Array.isArray(chartsResponse)) return null;
		return chartsResponse?.sections?.[0] ?? null;
	}, [chartsResponse]);

	const topLevelViewLayout: string | null = useMemo(() => {
		if (Array.isArray(chartsResponse)) return null;
		return chartsResponse?.view_layout ?? null;
	}, [chartsResponse]);

	const slideName: string | null = useMemo(() => {
		if (Array.isArray(chartsResponse)) return null;
		if (chartsResponse?.slide_name) return chartsResponse.slide_name;
		const firstChart = section?.charts ? flattenCharts(section.charts)[0] : null;
		if (firstChart?.slide_name) return firstChart.slide_name;
		return null;
	}, [chartsResponse, section]);

	const charts: ChartModel[] = useMemo(() => {
		if (Array.isArray(chartsResponse)) return chartsResponse;
		if (!section?.charts) return [];
		return flattenCharts(section.charts);
	}, [chartsResponse, section]);

	const chart_rules = useMemo(() => getChartRulesFromConfig(), []);

	const sortedCharts: ChartModel[] = useMemo(() => {
		if (charts.length === 0) return charts;
		const hasPositions = charts.every((c) => typeof c.view_position === "number");
		if (!hasPositions) return charts;
		return [...charts].sort((a, b) => (a.view_position ?? 0) - (b.view_position ?? 0));
	}, [charts]);

	const layoutCase = useMemo(() => resolveLayoutCase(section, sortedCharts, topLevelViewLayout), [section, sortedCharts, topLevelViewLayout]);
	const multiTile = useMemo(() => checkIsMultiTileSlide(sortedCharts, section?.layout_case), [sortedCharts, section]);

	// NEW — AskKuralInline no longer touches the global Redux flag;
	// chart components use isRenderedFromAskKuralInline directly instead
	useEffect(() => {
		if (skipMultiTileDispatch) return;
		dispatch(setIsMultiTileSlide(multiTile));
		return () => {
			if (!skipMultiTileDispatch) {
				dispatch(setIsMultiTileSlide(false));
			}
		};
	}, [dispatch, multiTile, skipMultiTileDispatch]);

	// ── Early returns ──

	if (chartsResponse?.error?.is_error) {
		return (
			<ChartError
				errorMessage={chartsResponse.error.error_message || "An error occurred while generating the chart"}
				setChartData={setChartData}
				setIsChartLoading={setIsChartLoading}
				errorType={chartsResponse.error.error_type}
				is_retry={chartsResponse?.error?.is_retry}
			/>
		);
	}

	if (sortedCharts.length === 0) {
		return <HorizontalBarChartSkeleton />;
	}

	const commonProps = {
		chartRules: chart_rules,
		isAISummaryAvailable,
		isChartView,
		setIsChartView,
	};

	// ── UPDATED: Wrap content with header + flex column ──

	// NEW — single AskKural charts render directly without MultiTileGrid
	// to avoid "Layout case not recognized" when fe_payload lacks layout metadata.
	// Multi-chart AskKural responses still use MultiTileGrid for proper tiling.
	if (multiTile || renderedFrom === ChartRenderedFromType.AskKuralInline) {
		const isSingleAskKuralChart = renderedFrom === ChartRenderedFromType.AskKuralInline && sortedCharts.length === 1;

		if (isSingleAskKuralChart) {
			return (
				<div className="dynamic-chart-render" style={{ display: "flex", flexDirection: "column", flex: "1 1 0%", minHeight: 0 }}>
					{slideName && <SlideHeader title={slideName} />}
					{/* InlineScaleWrapper provides ChartScaleProvider + constrains height
			    via flex:1 + overflow:hidden so the chart doesn't grow unbounded */}
					<InlineScaleWrapper>
						<ChartTile chartData={sortedCharts[0]} commonProps={commonProps} isCompact renderedFrom={renderedFrom} />
					</InlineScaleWrapper>
				</div>
			);
		}

		return (
			<div className="dynamic-chart-render">
				{slideName && <SlideHeader title={slideName} />}
				<MultiTileGrid charts={sortedCharts} layoutCase={layoutCase} commonProps={commonProps} renderedFrom={renderedFrom} />
			</div>
		);
	}

	return (
		<div className="dynamic-chart-render">
			<div className="dynamic-chart-render__single-chart-list h-full">
				{sortedCharts.map((chartData, index) => (
					<ChartTile key={chartData.analysis_id ?? index} chartData={chartData} commonProps={commonProps} isCompact={false} renderedFrom={renderedFrom} />
				))}
			</div>
		</div>
	);
};

export default DynamicChartRender;
